<template>
  <IonItem
    :id="id"
    @click.stop.prevent="disabled ? null : onClick()"
    class="serial-input-item"
    lines="full"
    :color="gray ? 'whitesmoke' : 'white'"
    :button="!disabled"
    :detail="false"
  >
    <div class="h-[60px] w-full flex flex-col justify-center pl-5">
      <div v-if="inputText" class="text-sm leading-tight text-gray-500 font-medium">{{ detectedFamily || title }}</div>
      <input
        class="appearance-none bg-transparent outline-none placeholder:text-gray-500 placeholder:font-medium disabled:text-gray-500 text-gray-700 font-medium"
        v-model="inputText"
        inputmode="none"
        ref="serialInputRef"
        :placeholder="title"
        :disabled="disabled"
        :spellcheck="false"
      />
    </div>
    <div slot="end" class="flex items-center gap-2 pr-5">
      <ValidationIcon :state="state" :errors="errors"></ValidationIcon>
      <BarcodeScannerButton :disabled="disabled" @on-scan="onBarcodeScan($event)"></BarcodeScannerButton>
    </div>
  </IonItem>
</template>

<script setup lang="ts">
import { IonItem } from '@ionic/vue';
import { computed, onMounted, ref, watch } from 'vue';
import ValidationIcon from '../ValidationIcon.vue';
import { FormControlError, FormControlState } from '@/composables/use-form-control';
import { useSerialKeyboard } from '@/stores/serial-keyboard.store';
import { DeviceTypeHelper, DeviceFamily } from '@ncss/models/dist/v3';
import { ConversionUtils } from '@ncss/models';
import BarcodeScannerButton from '@/components/BarcodeScannerButton.vue';
import { App } from '@capacitor/app';

const AcceptedCharacters = new Set(['A', 'B', 'C', 'D', 'E', 'F', '0', '1', '2', '3', '4', '5', '6', '7', '8', '9']);

const props = withDefaults(
  defineProps<{
    id: string;
    title?: string;
    gray?: boolean;
    errors?: FormControlError[];
    state?: FormControlState;
    disabled?: boolean;
    remoteReaderPortOptions?: {
      defaultToPort: 0 | 1 | 2;
      onIds?: { id: number; defaultToPort: 0 | 1 | 2 }[];
    };
    keepOpen?: boolean;
  }>(),
  { title: 'Serial Number', keepOpen: false }
);
const keyboard = useSerialKeyboard();

function onClick() {
  serialInputRef.value?.focus();
  keyboard.setFocus(props.id, {
    onBackspace,
    onKey,
  });
}
let listener: { remove: () => void };
const serialInputRef = ref<InstanceType<typeof HTMLInputElement>>();
const serialNumber = defineModel('serial', { type: Number });
const inputText = defineModel('text', { type: String, default: '' });
const detectedFamily = computed(() => {
  const chars = inputText.value?.toUpperCase()?.trim() || '';
  if (chars.startsWith('EA')) {
    return 'Remote Reader';
  } else if (chars.startsWith('BA') || chars.startsWith('BB')) {
    return 'Transceiver';
  } else if (chars.startsWith('DA')) {
    return 'Gateway';
  } else if (chars.startsWith('FA')) {
    return 'Leak Sensor';
  } else if (chars.startsWith('5A')) {
    return 'Next Meter';
  } else if (chars.startsWith('CA')) {
    return 'Repeater';
  } else if (chars.startsWith('7A')) {
    return 'Next Link';
  }
  return undefined;
});

function onBarcodeScan(scanned: number) {
  serialNumber.value = scanned;
}

onMounted(async () => {
  if (serialNumber.value && !inputText.value) {
    onSerialChange(serialNumber.value);
  } else if (inputText.value && !serialNumber.value) {
    onTextChange(inputText.value);
  }
  listener = await App.addListener('appStateChange', () => {
    keyboard.close();
    serialInputRef.value?.blur();
  });
});

watch(inputText, (txt, oldTxt) => onTextChange(txt, oldTxt));
watch(serialNumber, (num) => onSerialChange(num));
watch(serialNumber, (sn) => checkRRPort(sn), { immediate: true });
watch(
  () => props.state,
  (newState, oldState) => {
    if (oldState !== 'VALID' && newState === 'VALID' && keyboard.isInputFocused(props.id) && !props.keepOpen) {
      keyboard.close();
    }
  },
  { immediate: true }
);

function onTextChange(txt?: string, oldTxt?: string) {
  if (txt && txt.length > (oldTxt?.length || 0)) {
    const newChar = txt[txt.length - 1];
    if (!AcceptedCharacters.has(newChar) && !AcceptedCharacters.has(newChar.toUpperCase())) {
      inputText.value = oldTxt || '';
      return;
    }
  }
  const num = textToSN(txt);
  if (num !== serialNumber.value) {
    serialNumber.value = num;
  }
}

function onSerialChange(num?: number) {
  const txt = SNToText(num);
  if (txt !== inputText.value) {
    inputText.value = txt;
  }
}

function checkRRPort(sn?: number) {
  if (sn) {
    const family = DeviceTypeHelper.GetFamilyBySerialNumber(sn);
    if (family === DeviceFamily.REMOTE_READER && props.remoteReaderPortOptions) {
      const base = ConversionUtils.GetBaseRRSerial(sn);
      const foundId = props.remoteReaderPortOptions.onIds?.find(({ id, defaultToPort }) => {
        return (
          DeviceTypeHelper.GetFamilyBySerialNumber(id) === DeviceFamily.REMOTE_READER &&
          ConversionUtils.GetBaseRRSerial(id) === base
        );
      });
      const val = base + (foundId?.defaultToPort ?? props.remoteReaderPortOptions.defaultToPort);
      if (sn !== val) {
        serialNumber.value = val;
      }
    }
  }
}

function textToSN(text?: string) {
  return text ? parseInt(text, 16) : undefined;
}

function SNToText(sn?: number) {
  return sn || sn === 0 ? sn.toString(16).toUpperCase() : '';
}

function onKey(key: string) {
  if (inputText.value?.length === 8) {
    inputText.value = inputText.value.slice(0, inputText.value.length - 1) + key;
  } else {
    inputText.value += key;
  }
  serialInputRef.value?.focus();
}

function onBackspace() {
  if (inputText.value?.length) {
    inputText.value = inputText.value.slice(0, inputText.value.length - 1);
  }
  serialInputRef.value?.focus();
}
</script>

<style scoped>
ion-item.serial-input-item::part(native) {
  --border-width: 1px;
}

ion-item.serial-input-item {
  --padding-start: 0px;
  --inner-padding-start: 0px;
  --padding-end: 0px;
  --inner-padding-end: 0px;
}
</style>
